/**
 * ReorderSegmentItem - Draggable segment row for the reorder modal
 *
 * Displays a segment with:
 * - Drag handle (grip icon)
 * - Colored dot indicator
 * - Segment name
 * - Filter summary
 * - Default badge (for default segment, which is disabled)
 *
 * Uses react-beautiful-dnd's Draggable component.
 */

import { css } from 'aphrodite';
import { Draggable } from 'react-beautiful-dnd';
import type { SegmentInfo } from '../hooks/useReorderSegmentsModal';
import { styleSheet } from './styles/ReorderSegmentItemStyles';

interface ReorderSegmentItemProps {
	/** Segment information to display */
	segment: SegmentInfo;
	/** Index in the list for drag-and-drop */
	index: number;
}

/**
 * Grip/drag handle icon - matches the KanbanBoardColumnDefinitionGrip pattern
 */
function GripIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='currentColor'>
			<circle cx='5' cy='4' r='1.5' />
			<circle cx='11' cy='4' r='1.5' />
			<circle cx='5' cy='8' r='1.5' />
			<circle cx='11' cy='8' r='1.5' />
			<circle cx='5' cy='12' r='1.5' />
			<circle cx='11' cy='12' r='1.5' />
		</svg>
	);
}

export function ReorderSegmentItem({ segment, index }: ReorderSegmentItemProps) {
	// Default segment is not draggable
	if (segment.isDefault) {
		return (
			<div className={css(styleSheet.item, styleSheet.itemDisabled)}>
				<div className={css(styleSheet.dragHandle, styleSheet.dragHandleDisabled)}>
					<GripIcon />
				</div>
				<div className={css(styleSheet.colorDot)} style={{ backgroundColor: segment.caseColor }} />
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.name)}>{segment.name}</div>
					<div className={css(styleSheet.filterSummary)}>{segment.filterSummary}</div>
				</div>
				<span className={css(styleSheet.defaultBadge)}>Default</span>
			</div>
		);
	}

	return (
		<Draggable draggableId={segment.id} index={index}>
			{(provided, snapshot) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
					className={css(styleSheet.item, snapshot.isDragging && styleSheet.itemDragging)}
				>
					<div {...provided.dragHandleProps} className={css(styleSheet.dragHandle)}>
						<GripIcon />
					</div>
					<div className={css(styleSheet.colorDot)} style={{ backgroundColor: segment.caseColor }} />
					<div className={css(styleSheet.content)}>
						<div className={css(styleSheet.name)}>{segment.name}</div>
						<div className={css(styleSheet.filterSummary)}>{segment.filterSummary}</div>
					</div>
				</div>
			)}
		</Draggable>
	);
}
